import type { MediaTranscriptModel, MediaTranscriptQuantization } from '@/types/storage'
import {
  BROWSER_WHISPER_MODEL_LABELS,
  BROWSER_WHISPER_MODEL_OPTIONS,
  DEFAULT_BROWSER_WHISPER_MODEL,
  normalizeSelectableBrowserWhisperModel,
} from './browser-whisper-models'

export const DEFAULT_WHISPER_MODEL: MediaTranscriptModel = DEFAULT_BROWSER_WHISPER_MODEL
export const DEFAULT_WHISPER_QUANTIZATION: MediaTranscriptQuantization = 'hybrid'
export const DEFAULT_WHISPER_LANGUAGE = 'zh'
export const WHISPER_EMPTY_LANGUAGE_VALUE = ''
export const WHISPER_AUTO_LANGUAGE_VALUE = 'auto'

export const WHISPER_MODEL_LABELS: Record<MediaTranscriptModel, string> =
  BROWSER_WHISPER_MODEL_LABELS

export const WHISPER_MODEL_OPTIONS: ReadonlyArray<{
  value: MediaTranscriptModel
  label: string
}> = BROWSER_WHISPER_MODEL_OPTIONS

const WHISPER_LANGUAGE_SELECT_ALIASES = {
  'zh-cn': 'zh',
  'zh-hans': 'zh',
  'zh-hk': 'zh-hant',
  'zh-tw': 'zh-hant',
  'zh-hant': 'zh-hant',
} as const

const WHISPER_LANGUAGE_REQUEST_ALIASES = {
  'zh-cn': 'zh',
  'zh-hans': 'zh',
  'zh-hk': 'zh',
  'zh-tw': 'zh',
  'zh-hant': 'zh',
} as const

export function normalizeSelectableWhisperModel(
  model: MediaTranscriptModel | undefined,
): MediaTranscriptModel {
  return normalizeSelectableBrowserWhisperModel(model)
}

export const WHISPER_QUANTIZATION_OPTIONS: ReadonlyArray<{
  value: MediaTranscriptQuantization
  label: string
  description: string
}> = [
  {
    value: 'hybrid',
    label: '混合模式（推荐）',
    description: '默认推荐。内存占用和识别稳定性更均衡。',
  },
  {
    value: 'fp32',
    label: 'FP32（最高内存）',
    description: '适合内存充足时使用，走完整精度路径，稳定性最好。',
  },
  {
    value: 'fp16',
    label: 'FP16（WebGPU 较低内存）',
    description: '适合支持 WebGPU 的设备，在比 FP32 更低的内存占用下运行。',
  },
  {
    value: 'q8',
    label: 'Q8（紧凑内存）',
    description: '仅在内存较紧张时使用，识别稳定性通常不如混合模式。',
  },
  {
    value: 'q4',
    label: 'Q4（最低内存）',
    description: '极低内存兜底模式。字幕质量会更粗糙，调优空间也更有限。',
  },
]

export function getWhisperQuantizationOption(
  value: MediaTranscriptQuantization | undefined,
): (typeof WHISPER_QUANTIZATION_OPTIONS)[number] {
  const fallback = WHISPER_QUANTIZATION_OPTIONS[0]!
  return WHISPER_QUANTIZATION_OPTIONS.find((option) => option.value === value) ?? fallback
}

const WHISPER_LANGUAGE_NAMES = {
  en: 'english',
  zh: 'chinese',
  de: 'german',
  es: 'spanish',
  ru: 'russian',
  ko: 'korean',
  fr: 'french',
  ja: 'japanese',
  pt: 'portuguese',
  tr: 'turkish',
  pl: 'polish',
  ca: 'catalan',
  nl: 'dutch',
  ar: 'arabic',
  sv: 'swedish',
  it: 'italian',
  id: 'indonesian',
  hi: 'hindi',
  fi: 'finnish',
  vi: 'vietnamese',
  he: 'hebrew',
  uk: 'ukrainian',
  el: 'greek',
  ms: 'malay',
  cs: 'czech',
  ro: 'romanian',
  da: 'danish',
  hu: 'hungarian',
  ta: 'tamil',
  no: 'norwegian',
  th: 'thai',
  ur: 'urdu',
  hr: 'croatian',
  bg: 'bulgarian',
  lt: 'lithuanian',
  la: 'latin',
  mi: 'maori',
  ml: 'malayalam',
  cy: 'welsh',
  sk: 'slovak',
  te: 'telugu',
  fa: 'persian',
  lv: 'latvian',
  bn: 'bengali',
  sr: 'serbian',
  az: 'azerbaijani',
  sl: 'slovenian',
  kn: 'kannada',
  et: 'estonian',
  mk: 'macedonian',
  br: 'breton',
  eu: 'basque',
  is: 'icelandic',
  hy: 'armenian',
  ne: 'nepali',
  mn: 'mongolian',
  bs: 'bosnian',
  kk: 'kazakh',
  sq: 'albanian',
  sw: 'swahili',
  gl: 'galician',
  mr: 'marathi',
  pa: 'punjabi',
  si: 'sinhala',
  km: 'khmer',
  sn: 'shona',
  yo: 'yoruba',
  so: 'somali',
  af: 'afrikaans',
  oc: 'occitan',
  ka: 'georgian',
  be: 'belarusian',
  tg: 'tajik',
  sd: 'sindhi',
  gu: 'gujarati',
  am: 'amharic',
  yi: 'yiddish',
  lo: 'lao',
  uz: 'uzbek',
  fo: 'faroese',
  ht: 'haitian creole',
  ps: 'pashto',
  tk: 'turkmen',
  nn: 'nynorsk',
  mt: 'maltese',
  sa: 'sanskrit',
  lb: 'luxembourgish',
  my: 'myanmar',
  bo: 'tibetan',
  tl: 'tagalog',
  mg: 'malagasy',
  as: 'assamese',
  tt: 'tatar',
  haw: 'hawaiian',
  ln: 'lingala',
  ha: 'hausa',
  ba: 'bashkir',
  jw: 'javanese',
  su: 'sundanese',
  yue: 'cantonese',
} as const

const zhLanguageNames =
  typeof Intl !== 'undefined' && typeof Intl.DisplayNames !== 'undefined'
    ? new Intl.DisplayNames(['zh-Hans'], { type: 'language' })
    : null

function formatWhisperLanguageLabel(languageCode: string, languageName: string): string {
  if (languageCode === 'zh') {
    return '简体中文'
  }

  if (languageCode === 'en') {
    return '英语'
  }

  if (languageCode === 'zh-hant') {
    return '繁体中文'
  }

  if (languageCode === 'yue') {
    return '粤语'
  }

  const localizedName = zhLanguageNames?.of(languageCode)
  if (localizedName) {
    return localizedName
  }

  return languageName.replace(/\b\w/g, (char) => char.toUpperCase())
}

function normalizeWhisperLanguageToken(language: string | undefined): string | undefined {
  const trimmed = language?.trim().toLowerCase()
  return trimmed ? trimmed : undefined
}

const WHISPER_PINNED_LANGUAGE_OPTIONS = [
  {
    value: 'zh',
    label: '简体中文（中文）',
    keywords: ['中文', '简中', '简体', 'chinese', 'simplified chinese', 'mandarin'],
  },
  {
    value: 'en',
    label: '英语（English）',
    keywords: ['英语', '英文', 'english', 'en'],
  },
  {
    value: 'zh-hant',
    label: '繁体中文',
    keywords: ['繁体', '繁中', 'traditional chinese', 'zh-tw', 'zh-hk', 'zh-hant'],
  },
  {
    value: 'yue',
    label: '粤语',
    keywords: ['粤语', '广东话', 'cantonese', 'yue'],
  },
] as const

const WHISPER_PINNED_LANGUAGE_VALUES = new Set(
  WHISPER_PINNED_LANGUAGE_OPTIONS.map((option) => option.value),
)

export const WHISPER_LANGUAGE_OPTIONS: ReadonlyArray<{
  value: string
  label: string
  keywords?: readonly string[]
}> = [
  { value: WHISPER_AUTO_LANGUAGE_VALUE, label: '自动检测' },
  ...WHISPER_PINNED_LANGUAGE_OPTIONS,
  ...Object.entries(WHISPER_LANGUAGE_NAMES)
    .filter(([value]) => !WHISPER_PINNED_LANGUAGE_VALUES.has(value))
    .map(([value, label]) => ({
      value,
      label: formatWhisperLanguageLabel(value, label),
      keywords: [label, value],
    }))
    .sort((left, right) => left.label.localeCompare(right.label, 'zh-Hans')),
]

const WHISPER_LANGUAGE_VALUES = new Set(WHISPER_LANGUAGE_OPTIONS.map((option) => option.value))

export function getWhisperLanguageSelectValue(language: string | undefined): string {
  const normalized = normalizeWhisperLanguageToken(language)
  if (!normalized || normalized === WHISPER_AUTO_LANGUAGE_VALUE) {
    return DEFAULT_WHISPER_LANGUAGE
  }

  const selectValue = WHISPER_LANGUAGE_SELECT_ALIASES[normalized] ?? normalized
  return WHISPER_LANGUAGE_VALUES.has(selectValue) ? selectValue : DEFAULT_WHISPER_LANGUAGE
}

export function getWhisperLanguageSettingValue(value: string): string {
  if (value === WHISPER_AUTO_LANGUAGE_VALUE) {
    return WHISPER_EMPTY_LANGUAGE_VALUE
  }

  const normalized = normalizeWhisperLanguageToken(value)
  if (!normalized) {
    return WHISPER_EMPTY_LANGUAGE_VALUE
  }

  return WHISPER_LANGUAGE_REQUEST_ALIASES[normalized] ?? normalized
}

export function normalizeWhisperLanguage(language: string | undefined): string | undefined {
  const normalized = normalizeWhisperLanguageToken(language)
  if (!normalized) {
    return undefined
  }

  return WHISPER_LANGUAGE_REQUEST_ALIASES[normalized] ?? normalized
}
